import { createEffect, createSignal, untrack } from "solid-js";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { type ListWithMeta, updateListMutation } from "@listto/shared";

interface UseListTitleEditOptions {
  currentList: () => ListWithMeta | undefined;
  drizzle: () => PowerSyncDrizzleDB | null | undefined;
}

export const useListTitleEdit = (options: UseListTitleEditOptions) => {
  const [isEditingTitle, setIsEditingTitle] = createSignal(false);
  const [tempTitle, setTempTitle] = createSignal("");

  const [optimisticTitle, setOptimisticTitle] = createSignal<string | null>(null);

  let lastDbName = "";

  // Reset editing mode and caching variables when the active list changes
  createEffect(() => {
    const list = options.currentList();
    if (list) {
      untrack(() => {
        setIsEditingTitle(false);
        setOptimisticTitle(null);
        lastDbName = "";
      });
    }
  });

  createEffect(() => {
    const list = options.currentList();
    if (list) {
      const dbName = list.name ?? "";

      // If the database name matches our optimistic title, clear the optimistic override
      if (optimisticTitle() !== null && dbName === optimisticTitle()) {
        setOptimisticTitle(null);
      }

      // Sync tempTitle when not editing, or when the database name changes externally while editing
      if (!isEditingTitle() || dbName !== lastDbName) {
        setTempTitle(dbName);
        lastDbName = dbName;
      }
    }
  });

  const handleStartEditTitle = () => {
    const list = options.currentList();
    if (list) {
      setTempTitle(list.name ?? "");
      setIsEditingTitle(true);
    }
  };

  const handleSaveTitle = async () => {
    const list = options.currentList();
    const newTitle = tempTitle().trim();
    if (!list || !newTitle || newTitle === list.name) {
      setIsEditingTitle(false);
      return;
    }

    // Apply local optimistic update instantly
    setOptimisticTitle(newTitle);

    const dbInstance = options.drizzle();
    if (dbInstance) {
      await updateListMutation(dbInstance, list.id, { name: newTitle }).catch((err: unknown) => {
        console.error("Error updating list name:", err);
        // Rollback optimistic update if mutation fails
        setOptimisticTitle(null);
      });
    }
    setIsEditingTitle(false);
  };

  return {
    isEditingTitle,
    setIsEditingTitle,
    tempTitle,
    setTempTitle,
    handleStartEditTitle,
    handleSaveTitle,
    optimisticTitle,
  };
};
